import {Component, DestroyRef, effect, inject, OnInit, signal, Signal, WritableSignal} from '@angular/core';
import {FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AddressSearchComponent} from '../../shared/components/address-search/address-search.component';
import {HairstyleSearchComponent} from '../../shared/components/hairstyle-search/hairstyle-search.component';
import {DatePickerComponent} from '../../shared/components/date-picker/date-picker.component';
import {IconFilterComponent} from '../../shared/components/icons/icon-filter/icon-filter.component';
import {NgClass} from '@angular/common';
import {InputRadioComponent} from '../../shared/components/inputs/input-radio/input-radio.component';
import {HairdressersService} from '../../shared/services/hairdressers/hairdressers.service';
import {IHairdresserResponse} from '../../shared/interfaces/hairdresser.interface';
import {IconListComponent} from '../../shared/components/icons/icon-list/icon-list.component';
import {IconMapComponent} from '../../shared/components/icons/icon-map/icon-map.component';
import {
  HairdressersListSkeletonComponent
} from '../../shared/components/skeletons/hairdressers-list-skeleton/hairdressers-list-skeleton.component';
import {HairdressersListComponent} from '../../shared/components/hairdressers-list/hairdressers-list.component';
import {MapComponent} from './components/map/map.component';
import {placeOfReceptions} from '../../shared/constants/app.constants';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {MetasService} from '../../core/services/meta-service/metas.service';
import {getSEOMetaData} from '../../core/constants/seo.constants';

@Component({
  selector: 'braidsbook-hairdressers',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    AddressSearchComponent,
    HairstyleSearchComponent,
    DatePickerComponent,
    IconFilterComponent,
    NgClass,
    InputRadioComponent,
    IconListComponent,
    IconMapComponent,
    HairdressersListSkeletonComponent,
    HairdressersListComponent,
    MapComponent,
    TranslatePipe
  ],
  templateUrl: './hairdressers.component.html'
})

export class HairdressersComponent implements OnInit {
  #hairdressersService: HairdressersService = inject(HairdressersService);
  #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  #appRouter: Router = inject(Router);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #metasService: MetasService = inject(MetasService);
  #translateService: TranslateService = inject(TranslateService);

  queryAddress: string = this.#activatedRoute.snapshot.queryParams['address'] || '';
  queryHairstyle: string = this.#activatedRoute.snapshot.queryParams['hairstyle'] || '';
  searchForm: FormGroup = inject(NonNullableFormBuilder).group({
    location: new FormControl(this.queryAddress, {nonNullable: true}),
    hairstyle: new FormControl(this.queryHairstyle, {nonNullable: true}),
    availabilityDate: new FormControl('', {nonNullable: true})
  });
  isFilterOpen: boolean = false;

  activeView: WritableSignal<'list' | 'map'> = signal('list');
  isLoading: Signal<boolean> = this.#hairdressersService.isLoading;
  hairdressersResponse: Signal<IHairdresserResponse> = this.#hairdressersService.hairdressersResponse;

  constructor() {
    // Réinitialiser les filtres avant de déclencher l'effect
    // Cela évite que les filtres de la page précédente (comme hairstyle-details) persistent
    const queryParams = this.#activatedRoute.snapshot.queryParams;
    const queryAddress = queryParams['address'] || '';
    const queryHairstyle = queryParams['hairstyle'] || '';

    if (!queryAddress && !queryHairstyle) {
      // Réinitialiser les filtres si aucun filtre n'est présent dans les query params
      this.#hairdressersService.filters.set({
        hairstyle: '',
        activity_area: '',
        date_dispo: ''
      });
      this.#hairdressersService.page.set(1);
    } else {
      // Si des query params sont présents, les appliquer comme filtres
      this.#hairdressersService.filters.set({
        hairstyle: queryHairstyle || '',
        activity_area: queryAddress || '',
        date_dispo: ''
      });
      this.#hairdressersService.page.set(1);
    }

    // Déclencher le chargement après avoir défini les filtres
    effect((): void => {
      this.#hairdressersService.getHairdressers().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
    });
  }

  async ngOnInit(): Promise<void> {
    // Update SEO meta tags
    const metaData = getSEOMetaData('hairdressers', {
      url: `/hairdressers${this.#activatedRoute.snapshot.url.length > 0 ? this.#activatedRoute.snapshot.url : ''}`
    });
    if (metaData) {
      await this.#metasService.updateMetaTags(metaData);
    }

    // Update language attribute
    const currentLang = this.#translateService.currentLang || 'en';
    this.#metasService.updateLanguage(currentLang);

    // Écouter les changements de route pour réinitialiser les filtres si nécessaire
    this.#activatedRoute.queryParams.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(params => {
      const address = params['address'] || '';
      const hairstyle = params['hairstyle'] || '';

      if (!address && !hairstyle) {
        // Si les query params sont vides, réinitialiser les filtres
        const currentFilters = this.#hairdressersService.filters();
        if (currentFilters && (currentFilters.hairstyle || currentFilters.activity_area)) {
          this.#hairdressersService.filters.set({
            hairstyle: '',
            activity_area: '',
            date_dispo: ''
          });
          this.#hairdressersService.page.set(1);
        }
      }
    });
  }

  toggleFilters(): void {
    this.isFilterOpen = !this.isFilterOpen;
    if (this.isFilterOpen) {
      this.searchForm.addControl('placeOfReception', new FormControl('dom_utlr', {nonNullable: true}));
      this.searchForm.addControl('minTime', new FormControl('', {nonNullable: true}));
      this.searchForm.addControl('maxTime', new FormControl('', {nonNullable: true}));
    } else {
      this.searchForm.removeControl('placeOfReception');
      this.searchForm.removeControl('minTime');
      this.searchForm.removeControl('maxTime');
    }
  }

  onResetForm(): void {
    this.searchForm.reset();
    this.#hairdressersService.filters.update(() => ({
      hairstyle: '',
      activity_area: '',
      date_dispo: ''
    }));
    if (this.queryAddress.length || this.queryHairstyle.length) {
      this.searchForm.patchValue({
        location: '',
        hairstyle: ''
      });
      this.#appRouter.navigate(['/hairdressers', {queryParams: {page: 1, location: '', hairstyle: ''}}]).then();
    }
  }

  onSubmitForm(): void {
    const {hairstyle, location, availabilityDate, minTime, maxTime, placeOfReception} = this.searchForm.getRawValue();
    const payload = {
      hairstyle,
      activity_area: location,
      date_dispo: availabilityDate,
      ...(this.isFilterOpen && {
        h_min_dispo: minTime,
        h_max_dispo: maxTime,
        mobility: placeOfReception || 'dom_utlr'
      })
    };
    this.#hairdressersService.page.set(1);
    this.#hairdressersService.filters.update((_prev) => ({...payload}));
  }

  protected readonly placeOfReceptions = placeOfReceptions;
}
